import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import EditorDesign from '../../../organisms/editor-design/EditorDesign';
import WalkthroughRichText from './WalkthroughRichText';
import WalkthroughAdvanceControl from './WalkthroughAdvanceControl';
import { getLayerConfig, updateStep, allSteps, selectLayers } from '../../../../redux/stepsSlice';
import { getActiveStep, selectActiveLayer } from '../../../../redux/toolsSlice';
import { getMaterial } from '../../../../redux/materialSlice';

// Walkthrough Editor v2 — collapsible contextual inspector (BL-139 / redesign).
// One panel that swaps contents by selection scope:
//   • element (hotspot)     — the step's Instruction / Notes / Advance + the
//                             hotspot's Style, all together (matches the mockup).
//   • element (non-hotspot) — that element's Style only (EditorDesign).
//   • step                  — Step name + Instruction / Notes / Advance.
//   • walkthrough           — name + appearance/theme.
// Per the 2026-07-31 decision, instruction/notes/advance are STORED ON THE STEP;
// we simply surface them when the step's hotspot is selected. Per-hotspot text
// and branching is a future project (BL-160).

const TYPE_LABELS = {
  hotspot: 'Hotspot',
  square: 'Box',
  line: 'Line',
  text: 'Text',
  textInput: 'Text input',
  dropdown: 'Dropdown',
  image: 'Image',
  button: 'Button'
};

const HotspotIc = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3l7 18 2.5-7L20 11 3 3z" />
  </svg>
);
const ElementIc = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="2" />
  </svg>
);
const StepIc = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M7 20h10" />
  </svg>
);
const WalkthroughIc = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
  </svg>
);

const WalkthroughInspector = ({ onClose, appearanceOnly }) => {
  const dispatch = useDispatch();
  const layerConfig = useSelector((state) => getLayerConfig(state));
  const activeStep = useSelector((state) => getActiveStep(state));
  const activeLayer = useSelector((state) => selectActiveLayer(state));
  const material = useSelector((state) => getMaterial(state));
  const steps = useSelector((state) => allSteps(state)) || [];
  const stepLayers = useSelector((state) =>
    activeStep && activeStep.id ? selectLayers(state, activeStep.id) : []
  );

  const [scope, setScope] = useState('step'); // when no element: 'step' | 'walkthrough'

  // Is an element selected? layerConfig carries the selected element's design
  // sections; activeLayer carries its id. Either present means "element scope".
  const layerId = activeLayer && activeLayer.layerId;
  const hasElement = (Array.isArray(layerConfig) && layerConfig.length > 0) || !!layerId;
  const selectedEl = layerId ? (stepLayers || []).find((l) => l.id === layerId) : null;
  const elType = selectedEl && selectedEl.type ? selectedEl.type : 'element';
  const isHotspot = elType === 'hotspot';

  // Preview mode pins the panel to Appearance so theme edits are always visible.
  const effScope = appearanceOnly ? 'walkthrough' : (hasElement ? 'element' : scope);

  const stepIndex = activeStep ? steps.findIndex((s) => s.id === activeStep.id) : -1;
  const stepNo = stepIndex >= 0 ? stepIndex + 1 : null;
  const stepName = (activeStep && (activeStep.title || activeStep.name)) || '';

  const onStepName = (e) => {
    if (!activeStep) return;
    dispatch(updateStep({ ...activeStep, title: e.target.value }));
  };
  const onDescChange = (html) => {
    if (!activeStep) return;
    dispatch(updateStep({ ...activeStep, description: html }));
  };
  const onNotesChange = (html) => {
    if (!activeStep) return;
    dispatch(updateStep({ ...activeStep, notes: html }));
  };
  const onAdvanceChange = (advance) => {
    if (!activeStep) return;
    dispatch(updateStep({ ...activeStep, advance }));
  };

  // Header (icon, title, sub) depends on the effective scope.
  let headIcon = <StepIc />;
  let headTitle = stepName || 'Step';
  let headSub = 'Step settings';
  if (effScope === 'element') {
    headIcon = isHotspot ? <HotspotIc /> : <ElementIc />;
    headTitle = TYPE_LABELS[elType] || 'Element';
    headSub = stepNo ? `Step ${stepNo} · Selected element` : 'Selected element';
  } else if (effScope === 'walkthrough') {
    headIcon = <WalkthroughIc />;
    headTitle = (material && material.name) || 'Appearance';
    headSub = 'Appearance';
  }

  // The step Instruction / Notes / Advance block — reused by the step scope and
  // by the hotspot element scope.
  const stepFields = (
    <>
      <div className="un-we-fld">
        <label>Instruction</label>
        <WalkthroughRichText
          key={(activeStep && activeStep.id ? activeStep.id : 'none') + '-desc'}
          value={(activeStep && activeStep.description) || ''}
          onChange={onDescChange}
          placeholder="What should the learner do in this step?"
          minHeight={104}
        />
        <p className="un-we-hint">Shown to the learner in the step tooltip. Select text to format, or insert a token to personalize.</p>
      </div>
      <div className="un-we-fld">
        <label>Additional notes <span className="un-we-opt">· optional</span></label>
        <WalkthroughRichText
          key={(activeStep && activeStep.id ? activeStep.id : 'none') + '-notes'}
          value={(activeStep && activeStep.notes) || ''}
          onChange={onNotesChange}
          placeholder="Add extra context or tips for this step…"
          minHeight={84}
        />
        <p className="un-we-hint">Extra guidance shown alongside the tooltip. Learners see this only if you add it.</p>
      </div>
      <div className="un-we-fld">
        <label>Advance to the next step</label>
        <WalkthroughAdvanceControl value={activeStep && activeStep.advance} onChange={onAdvanceChange} />
        <p className="un-we-hint">
          Pick one or both. Choosing both lets learners advance by clicking the hotspot <i>or</i> a Next button.
          <b> Auto</b> advances on its own after the delay — for steps with nothing to click.
        </p>
      </div>
    </>
  );

  return (
    <aside className="un-we-insp">
      <div className="un-we-insp__head">
        <span className="un-we-insp__ic">{headIcon}</span>
        <div className="un-we-insp__ttl">
          <div className="t">{headTitle}</div>
          <div className="s">{headSub}</div>
        </div>
        {!appearanceOnly && (
          <button type="button" className="un-we-insp__cx" title="Hide panel" aria-label="Hide inspector panel" onClick={onClose}>
            ›
          </button>
        )}
      </div>

      {!hasElement && !appearanceOnly && (
        <div className="un-we-insp__scopes">
          <button type="button" className={scope === 'step' ? 'on' : ''} onClick={() => setScope('step')}>Step</button>
          <button type="button" className={scope === 'walkthrough' ? 'on' : ''} onClick={() => setScope('walkthrough')}>Appearance</button>
        </div>
      )}

      <div className="un-we-insp__body">
        {/* Element selected — hotspot shows the step fields + its style */}
        {effScope === 'element' && isHotspot && (
          <>
            {stepFields}
            <div className="un-we-sec">
              <div className="un-we-sec__h">Style</div>
              <EditorDesign />
            </div>
          </>
        )}
        {effScope === 'element' && !isHotspot && (
          <div className="un-we-sec">
            <div className="un-we-sec__h">Style</div>
            <EditorDesign />
          </div>
        )}

        {/* Nothing selected — step scope */}
        {effScope === 'step' && (
          <>
            <div className="un-we-fld">
              <label>Step name</label>
              <input className="un-we-in" value={stepName} onChange={onStepName} placeholder="Name this step" />
            </div>
            {stepFields}
          </>
        )}

        {/* Appearance scope (also forced in preview mode) */}
        {effScope === 'walkthrough' && (
          <>
            <div className="un-we-fld">
              <label>Walkthrough name</label>
              <input className="un-we-in" value={(material && material.name) || ''} readOnly />
              <p className="un-we-hint">Rename from the title at the top of the editor.</p>
            </div>
            <div className="un-we-sec">
              <div className="un-we-sec__h">Appearance</div>
              <EditorDesign />
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

export default WalkthroughInspector;
